import * as React from 'react';
import * as autoBind from 'react-autobind';
import * as classNames from 'classnames';
import {PopoverButton, Glyphs, TscPopoverOrigin, ControlButton, TscButtonType} from '@techsmith/tsc-cloud-style-guide';
import Strings from '../../globals/Strings';
import {ModalStates} from '../../globals/Constants';
import ReviewService from '../../services/ReviewService/ReviewService';
import UserMessaging from '../../utils/UserMessaging';
import GoogleAnalyticsService from '../../services/GoogleAnalyticsService/GoogleAnalyticsService';
import Constants from '../../globals/Constants';
import Store from '../../redux/Store';
import ModalEventHandlersModel, {nullModalEventHandlersFactory} from '../../types/ModalEventHandlers';
import Themeable from '../../types/Themeable';
import Actions from '../../redux/Actions';
import Config from '../../globals/Config';

export default class ReviewOptions extends React.Component<ReviewOptionsProps, {}> {
   popoverButtonRef: any;
   constructor(props: ReviewOptionsProps) {
      super(props);
      autoBind(this);
   }

   async onExportComments() {
      const {reviewId} = this.props;
      const userId = Store.getState().currentUserId;

      try {
         await ReviewService.downloadCsvData(reviewId);
         GoogleAnalyticsService.trackExportComments(userId, Constants.googleAnalytics.exportComments.actions.exportFromPopUpMenu);
      } catch {
         UserMessaging.warn(Strings.errors.failedToGetArchive);
      }
   }

   onDeleteReview() {
      const {reviewId, setReviewInactive, setModalState, hasComments} = this.props;

      const modalEventHandlers = {
         onCustomEvent: async (customEventName: string): Promise<void> => {
            switch (customEventName) {
               case 'downloadArchiveCsv':
                  try {
                     await ReviewService.downloadCsvData(reviewId);
                     GoogleAnalyticsService.trackExportComments(Config.user.techSmithId, Constants.googleAnalytics.exportComments.actions.exportFromDeleteModal);
                  } catch {
                     UserMessaging.warn(Strings.errors.failedToGetArchive);
                  }
            }
         },
         onConfirm: async () => {
            try {
               setReviewInactive(reviewId, true);
               await ReviewService.deleteReview(reviewId);
               const deleteReviewAction = hasComments ? Constants.googleAnalytics.deleteReview.actions.reviewHasComments : Constants.googleAnalytics.deleteReview.actions.reviewHasNoComments;
               GoogleAnalyticsService.trackDeleteReview(Config.user.techSmithId, deleteReviewAction);
            } catch {
               setReviewInactive(reviewId, false);
               UserMessaging.warn(Strings.errors.failedDeleteReview);
            }
         }
      };

      const modalState = hasComments ? ModalStates.deleteReviewWithComments : ModalStates.deleteReview;
      setModalState(modalState, modalEventHandlers);
   }

   onLeaveReview() {
      const {reviewId, setReviewInactive, setModalState} = this.props;

      const modalEventHandlers = {
         onConfirm: async () => {
            try {
               setReviewInactive(reviewId, true);
               await ReviewService.leaveReview(reviewId);
               GoogleAnalyticsService.trackLeaveReview(Config.user.techSmithId);
            } catch {
               setReviewInactive(reviewId, false);
               UserMessaging.warn(Strings.errors.failedLeaveReview);
            }
         }
      };

      setModalState(ModalStates.leaveReview, modalEventHandlers);
   }

   onShareReview(): void {
      const {reviewId, latestVersionNumber, setModalState} = this.props;
      Store.dispatch(Actions.setReviewId(reviewId, latestVersionNumber));
      setModalState(ModalStates.shareReview, nullModalEventHandlersFactory());
   }

   render() {
      const {isOwner, themeName, hasComments, isListView} = this.props;
      const exportCommentsClasses = classNames('setting', 'export-comments-button', {'btn-disabled': !hasComments});

      const optionsMenu = (
         <span>
            <a className="setting share-button" onClick={this.onShareReview}>{Strings.labels.share}</a>
            <a className="setting delete-review-button" onClick={this.onDeleteReview}>{Strings.labels.delete}</a>
            <a className={exportCommentsClasses} onClick={this.onExportComments}>{Strings.labels.exportComments}</a>
         </span>
      );

      return (
         <div className="options-menu settings-review-options-menu">
            {isOwner ? <PopoverButton
               buttonGlyph={<Glyphs.Ellipsis24x24 />}
               buttonTitle={Strings.labels.options}
               themeName={themeName}
               popoverMenu={optionsMenu}
               origin={isListView ? TscPopoverOrigin.left : TscPopoverOrigin.topRight}
               showArrow={false}
               testId="review-options-button"
               style={{width: '2rem'}}
               closeMenuOnClick={true}
            /> : <ControlButton
               glyph={<Glyphs.CircleRemove16x16 />}
               buttonType={TscButtonType.tertiary}
               title={Strings.labels.leaveReview}
               themeName={themeName}
               width="2rem"
               testId="review-options-leave-button"
               onClick={this.onLeaveReview}
            />}
         </div>
      );
   }
}

export interface ReviewOptionsProps extends Themeable {
   reviewId: string;
   isOwner: boolean;
   hasComments: boolean;
   latestVersionNumber: number;
   isListView: boolean;
   setModalState(modalState: number, modalEventHandlers: ModalEventHandlersModel): void;
   setReviewInactive(id: string, visible: boolean): void;
}
